import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, shareReplay, startWith, switchMap, tap, filter } from 'rxjs/operators';
import { AuthenticationService } from '../authentication/services/authentication.service';
import { UserSettingsService } from '../services/user-settings.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { CreateUserComponent } from './create-user/create-user.component';
import { ViewUserComponent } from './view-user/view-user.component';
import { SnackbarService } from '../services/snackbar.sevice';
import { SpinnerService } from '../services/spinner.service';
import { MatTableDataSource } from '@angular/material/table';
import { Constants } from '../models/constants';
import { MessageBoxService } from '../messagebox/services/messagebox.service';
import { MessageBoxButtons, MessageBoxIcon, MessageBoxResult } from '../messagebox/messagebox.model';
import { Role, RoleEnum, ProblemDetails, UserSummaryV2 } from '../api-module/user-management-api-client-v2';
import { OrgSiteSelectionService } from '../shared/org-site-selection/org-site-selection.service';
import { Org } from '../models';
import { CommonUtilities } from '@app/shared/common-utilities';
import { SettingUtilities } from '@app/shared/setting-utilities';
import { SettingType } from '@app/api-module/control-centre-api-client';

@Component({
    selector: 'app-my-hospital-users',
    templateUrl: './my-hospital-users.component.html',
    styleUrls: ['./my-hospital-users.component.scss']
})
export class MyHospitalUsersComponent implements OnInit, OnDestroy {
    @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;

    private readonly subscriptions = new Subscription();

    private readonly roles = new BehaviorSubject<Role[]>(null);
    orgUsers$: Observable<UserSummaryWithIcon[]>;
    fetchTrigger$ = new BehaviorSubject(null);

    alwaysDisplayedColumns: string[] = [
        'title',
        'firstName',
        'lastName',
        'emailAddress',
        'defaultContactNumber',
        'defaultContactExtension',
        'role'
    ];

    mfaColumn: string = 'mfaEnabled';

    displayedColumns: string[];
    selectedRow: UserSummaryWithIcon;

    contextMenuPosition = { x: '0px', y: '0px' };
    hiddenRoles: RoleEnum[] = [];

    dataSource: MatTableDataSource<UserSummaryWithIcon>;

    constructor(
        public dialog: MatDialog,
        private readonly userSettingsService: UserSettingsService,
        private readonly authenticationService: AuthenticationService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly spinnerService: SpinnerService,
        private readonly snackbarService: SnackbarService,
        private readonly messageBoxService: MessageBoxService
    ) {
        this.initOrgUsers();
        this.initRoles();
    }

    ngOnInit(): void {
        this.spinnerService.show();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    onRowContextMenu(event: MouseEvent, row: UserSummaryWithIcon): void {
        this.selectedRow = row;

        event.preventDefault();
        this.contextMenuPosition.x = event.clientX + 'px';
        this.contextMenuPosition.y = event.clientY + 'px';
        this.contextMenu.menu.focusFirstItem('mouse');
        this.contextMenu.openMenu();
    }

    onContextMenuRemoveUser(): void {
        const user = this.selectedRow;

        const title = 'Please Confirm';
        const message = 'Are you sure you want to remove user?';
        const information = null;
        const orgCode = this.orgSiteSelectionService.getOrgSelectedValue().orgCode;

        this.messageBoxService
            .showMessageBox({
                title,
                message,
                information,
                icon: MessageBoxIcon.Confirmation,
                buttons: MessageBoxButtons.RemoveCancel
            })
            .subscribe(result => {
                if (result === MessageBoxResult.Yes) {
                    this.userSettingsService.removeUser(user.id, orgCode).subscribe({
                        next: _ => {
                            this.snackbarService.openSnackBar(
                                'The user has been successfully removed.',
                                SnackbarService.SNACK_BAR_CLASS_SUCCESS
                            );
                            this.triggerFetch();
                        },
                        error: response => {
                            if (response instanceof HttpErrorResponse && response.status === 404) {
                                this.snackbarService.openSnackBar(
                                    'Cannot find the user. The user may have been removed by ' +
                                        'another administrator. Please refresh the page. If the issue persists, please contact Everlight support team.',
                                    SnackbarService.SNACK_BAR_CLASS_ALERT
                                );
                                return;
                            } else if (response instanceof ProblemDetails) {
                                this.snackbarService.openSnackBar(
                                    (response as ProblemDetails).detail,
                                    SnackbarService.SNACK_BAR_CLASS_ALERT,
                                    15000
                                );
                                return;
                            }
                            this.snackbarService.openSnackBar(
                                Constants.UnableToContactServerMessage,
                                SnackbarService.SNACK_BAR_CLASS_ALERT
                            );
                        }
                    });
                }
            });
    }

    onRowClick(row: UserSummaryWithIcon): void {
        this.selectedRow = row;

        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = '100vw';
        dialogConfig.maxHeight = this.calculateDialogMaxHeight();
        dialogConfig.autoFocus = true;
        dialogConfig.panelClass = 'view-user-dialog';
        dialogConfig.data = {
            id: row.id,
            roles: this.roles.value
        };

        const dialogRef = this.dialog.open(ViewUserComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(_ => {
            this.triggerFetch();
        });
    }

    /**
     * This is to fix an issue that in small screen, the popup is overlapping with header.
     * Header's height will always be 67.5px, but popup's height 90vh will not leave enough space at top and bottom in small screen.
     * Hence it's needed to calculate a minimal value between 90vh and screen height minus header's height * 2 (the bottom space included)
     */
    private calculateDialogMaxHeight() {
        const headerHeight = document.querySelector<HTMLElement>('.navbar').offsetHeight;
        return Math.min(window.innerHeight * 0.9, window.innerHeight - headerHeight * 2) + 'px';
    }

    private triggerFetch() {
        this.spinnerService.show();
        this.fetchTrigger$.next(null);
    }

    private updateHiddenRoles(role: RoleEnum, shouldHide: boolean): void {
        this.hiddenRoles = shouldHide ? [...new Set([...this.hiddenRoles, role])] : this.hiddenRoles.filter(r => r !== role);
    }

    private initOrgUsers() {
        const selectedOrg$: Observable<Org> = this.orgSiteSelectionService
            .getOrgSelected$()
            .pipe(filter(org => org != null && org != undefined));

        this.subscriptions.add(
            this.orgSiteSelectionService.getOrgSelectedSettings$().subscribe(settings => {
                const newColumns = [...this.alwaysDisplayedColumns];
                const indexToInsertAfter = newColumns.findIndex(column => CommonUtilities.stringEquals(column, 'defaultContactExtension'));
                // We don't check sites here as MFA is only org wide.
                if (SettingUtilities.isSettingEnabled(settings, SettingType.MfaEnabled)) {
                    newColumns.splice(indexToInsertAfter, 0, this.mfaColumn);
                }
                this.displayedColumns = newColumns;

                this.updateHiddenRoles(
                    RoleEnum.DemandManagement,
                    !SettingUtilities.isSettingEnabled(settings, SettingType.DemandManagement)
                );
            })
        );

        const sortedUsers$ = combineLatest([this.fetchTrigger$, selectedOrg$])
            .pipe(
                switchMap(([_, org]) => this.userSettingsService.getUsersByOrgV2(org.orgCode)),
                catchError(err => {
                    this.spinnerService.hide();
                    return throwError(err);
                })
            )
            .pipe(
                map(users => users.sort((a, b) => a.surname.localeCompare(b.surname) || a.firstName.localeCompare(b.firstName))),
                tap(_ => this.spinnerService.hide())
            );

        this.orgUsers$ = combineLatest([sortedUsers$, this.orgSiteSelectionService.getOrgSelectedSettings$()]).pipe(
            map(([users, clientSettings]) => {
                const imagingOverviewEnabled = SettingUtilities.isSettingEnabled(clientSettings, SettingType.ImagingOverviewEnabled);
                const clientReferralCreationEnabled = SettingUtilities.isSettingEnabled(clientSettings, SettingType.ClientReferralCreation);
                return users.map(user => {
                    const userRoles = user.roles ? [...user.roles] : [];
                    const indexOfImagingRequestViewer = userRoles.findIndex(role => role === RoleEnum.ImagingRequestViewer);
                    if (!imagingOverviewEnabled && indexOfImagingRequestViewer !== -1) {
                        userRoles.splice(indexOfImagingRequestViewer, 1);
                    }
                    const indexOfImagingRequestEditor = userRoles.findIndex(role => role === RoleEnum.ImagingRequestEditor);
                    if (!clientReferralCreationEnabled && indexOfImagingRequestEditor !== -1) {
                        userRoles.splice(indexOfImagingRequestEditor);
                    }
                    const mappedUser = new UserSummaryWithIcon({
                        ...user,
                        roles: userRoles
                    });
                    mappedUser.svgIcon = MyHospitalUsersComponent.getSvgIcon(mappedUser.mfaEnabled);

                    return mappedUser;
                });
            })
        );

        this.subscriptions.add(
            combineLatest([selectedOrg$, this.orgUsers$]).subscribe(([org, users]) => {
                const currentUser = this.authenticationService.getCurrentUser();
                if (!currentUser.hasRole(RoleEnum.Admin, org.orgCode)) {
                    users = users.filter(user => user.id !== currentUser.id);
                }
                this.dataSource = new MatTableDataSource(users);
                this.dataSource.filterPredicate = (user, filterText: string): boolean =>
                    user.firstName.toLowerCase().includes(filterText) ||
                    user.surname.toLowerCase().includes(filterText) ||
                    user.title?.toLowerCase().includes(filterText) ||
                    user.emailAddress?.toLowerCase().includes(filterText) ||
                    user.defaultContactNumber?.toLowerCase().includes(filterText) ||
                    user.defaultContactExtension?.toLowerCase().includes(filterText) ||
                    this.getRolesString(user.roles).toLowerCase().includes(filterText);
            })
        );
    }

    private initRoles() {
        const roles = this.userSettingsService.getRoles().pipe(startWith([] as Role[]), shareReplay());
        this.subscriptions.add(
            combineLatest([roles, this.orgSiteSelectionService.getOrgSelectedSettings$()]).subscribe({
                next: ([roles, settings]) => {
                    const rolesCopy = [...roles];
                    const imagingRequestViewerRoleIndex = rolesCopy.findIndex(role => role.name === 'Imaging Request Viewer');
                    if (
                        !SettingUtilities.isSettingEnabled(settings, SettingType.ImagingOverviewEnabled) &&
                        imagingRequestViewerRoleIndex >= 0
                    ) {
                        rolesCopy.splice(imagingRequestViewerRoleIndex, 1);
                    }
                    const imagingRequestEditorRoleIndex = rolesCopy.findIndex(role => role.name === 'Imaging Request Editor');
                    if (
                        !SettingUtilities.isSettingEnabled(settings, SettingType.ClientReferralCreation) &&
                        imagingRequestEditorRoleIndex >= 0
                    ) {
                        rolesCopy.splice(imagingRequestEditorRoleIndex, 1);
                    }
                    const demandManagementRoleIndex = rolesCopy.findIndex(role => role.name === 'Demand Management');
                    if (!SettingUtilities.isSettingEnabled(settings, SettingType.DemandManagement) && demandManagementRoleIndex >= 0) {
                        rolesCopy.splice(demandManagementRoleIndex, 1);
                    }
                    this.roles.next(rolesCopy);
                },
                error: _ => {
                    this.snackbarService.openSnackBar(Constants.CannotFindRoles, SnackbarService.SNACK_BAR_CLASS_ALERT);
                }
            })
        );
    }

    private roleDisplayName(role: string | RoleEnum): string {
        let displayName = '';
        switch (role) {
            case 'Admin':
            case RoleEnum.Admin:
                displayName = 'Admin';
                break;
            case 'OrderEntry':
            case RoleEnum.GeneralAccess:
                displayName = 'General Access';
                break;
            case RoleEnum.ClinicalFeedback:
                displayName = 'Clinical Feedback';
                break;
            case RoleEnum.ImagingRequestViewer:
                displayName = 'Imaging Request Viewer';
                break;
            case RoleEnum.ImagingRequestEditor:
                displayName = 'Imaging Request Editor';
                break;
            case RoleEnum.DemandManagement:
                displayName = 'Demand Management';
                break;
            default:
                displayName = role;
                break;
        }
        return displayName;
    }

    getRolesString(roles: RoleEnum[]): string {
        return (
            roles
                ?.filter(role => !this.hiddenRoles.includes(role))
                .map(role => this.roleDisplayName(role))
                .join(', ') ?? ''
        );
    }

    newUser(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = '100vw';
        dialogConfig.maxHeight = this.calculateDialogMaxHeight();
        dialogConfig.autoFocus = true;
        dialogConfig.panelClass = 'view-user-dialog';
        dialogConfig.data = {
            roles: this.roles.value
        };

        const dialogRef = this.dialog.open(CreateUserComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(_ => {
            this.triggerFetch();
        });
    }

    resetSearch(inputFilter: HTMLInputElement): void {
        inputFilter.value = '';
        inputFilter.dispatchEvent(new Event('input'));
    }

    applyFilter(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value;
        if (this.dataSource) {
            this.dataSource.filter = filterValue.trim().toLowerCase();
        }
    }

    static getSvgIcon(mfaEnabled: boolean): string {
        return mfaEnabled ? 'green-checkmark' : 'red-cross';
    }
}

export class UserSummaryWithIcon extends UserSummaryV2 {
    svgIcon: string;
}
